import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { Icon } from './Icon';
import { sectorsData } from '../../data/sectors';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<typeof sectorsData[string][]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchQuery = query.toLowerCase();
    const filtered = Object.values(sectorsData).filter(
      (sector) =>
        sector.title.toLowerCase().includes(searchQuery) ||
        sector.description.toLowerCase().includes(searchQuery) ||
        sector.features.some((f) => f.toLowerCase().includes(searchQuery))
    );

    setResults(filtered);
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    }
  };

  const handleSelect = (sector: typeof sectorsData[string]) => {
    navigate(`/sectors/${sector.id}`);
    onClose();
    setQuery('');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal */}
      <div className="fixed top-[72px] left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4 animate-in fade-in slide-in-from-top-2 duration-200">
        <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
          <Icon name="search" className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search sectors and services..."
            className="flex-1 outline-none text-lg placeholder:text-gray-400"
          />
          <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query && results.length === 0 && (
            <div className="py-16 px-6 text-center">
              <Icon name="search" className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-600">No results found</p>
            </div>
          )}

          {results.map((sector, index) => (
            <button
              key={sector.id}
              onClick={() => handleSelect(sector)}
              className={cn(
                'w-full text-left px-6 py-4 transition-colors',
                'flex items-center gap-4 group',
                'border-b border-gray-50 last:border-0',
                selectedIndex === index 
                  ? 'bg-orange-50' 
                  : 'hover:bg-gray-50'
              )}
            >
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                selectedIndex === index
                  ? 'bg-orange-500'
                  : 'bg-gray-100'
              )}>
                <Icon 
                  name={sector.icon} 
                  className={cn(
                    'w-5 h-5',
                    selectedIndex === index ? 'text-white' : 'text-gray-600'
                  )} 
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 mb-0.5">
                  {sector.title}
                </div>
                <div className="text-sm text-gray-500 line-clamp-1">
                  {sector.description}
                </div>
              </div>
              <Icon
                name="arrow-right"
                className="w-4 h-4 text-gray-400"
              />
            </button>
          ))}
        </div>

        {/* Footer */}
        {results.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
            <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded">↓</kbd>
                <span>to navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded">↵</kbd>
                <span>to select</span>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};
